import { useMemo } from "react";
import { Case } from "@/lib/useRealtimeCases";
import { supabase } from "@/lib/supabaseClient";
import { useSelectedCenters } from "@/lib/SelectedCentersContext";
import { diffParisCalendarDays, getParisDateKey } from "@/lib/parisDateTime";

interface TodoTodayModalProps {
  isOpen: boolean;
  onClose: () => void;
  cases: Case[];
  onCaseClick: (caseData: Case) => void;
}

interface TodoSection {
  title: string;
  icon: string;
  priority: "critical" | "high" | "medium" | "info";
  cases: Case[];
  description: string;
}

export function TodoTodayModal({ isOpen, onClose, cases, onCaseClick }: TodoTodayModalProps) {
  const { selectedCenters } = useSelectedCenters();

  // Filtrer les dossiers par centres sélectionnés
  const filteredCases = useMemo(() => {
    if (selectedCenters.length === 0) {
      return cases; // Si aucun centre sélectionné, afficher tous
    }
    return cases.filter(c => selectedCenters.includes(c.center || ""));
  }, [cases, selectedCenters]);

  // Calculer les sections avec leurs dossiers
  const todoSections = useMemo(() => {
    const now = new Date();
    const sections: TodoSection[] = [];

    // Helper: Calculer les jours avant un événement
    const getDaysUntilEvent = (eventDate: string | null): number | null => {
      if (!eventDate) return null;
      return diffParisCalendarDays(now, eventDate);
    };

    // Helper: Calculer les heures depuis la création
    const getHoursSinceCreation = (createdAt: string): number => {
      const created = new Date(createdAt);
      const diffMs = now.getTime() - created.getTime();
      return Math.floor(diffMs / (1000 * 60 * 60));
    };

    // 🚨 SECTION 1: Événement imminent sans confirmation
    const urgentNoConfirmation = filteredCases.filter((c) => {
      const daysUntilEvent = getDaysUntilEvent(c.event_date);
      const hasClientResponse = c.last_client_response_at && c.last_quote_sent_at
        ? new Date(c.last_client_response_at) > new Date(c.last_quote_sent_at)
        : false;

      return (
        c.status === "Devis envoyé" &&
        daysUntilEvent !== null &&
        daysUntilEvent >= 0 &&
        daysUntilEvent < 3 &&
        !hasClientResponse
      );
    });

    if (urgentNoConfirmation.length > 0) {
      sections.push({
        title: "Événement imminent sans confirmation",
        icon: "🚨",
        priority: "critical",
        cases: urgentNoConfirmation.sort((a, b) => {
          const daysA = getDaysUntilEvent(a.event_date) || 999;
          const daysB = getDaysUntilEvent(b.event_date) || 999;
          return daysA - daysB;
        }),
        description: "Devis envoyé mais aucune réponse client - Événement dans moins de 3 jours",
      });
    }

    // ⏰ SECTION 2: Dossiers non traités (>24h)
    const nonTraitesAnciens = filteredCases.filter((c) => {
      const hoursSinceCreation = getHoursSinceCreation(c.created_at);
      return c.status === "Non traité" && hoursSinceCreation > 24;
    });

    if (nonTraitesAnciens.length > 0) {
      sections.push({
        title: "Dossiers non traités (>24h)",
        icon: "⏰",
        priority: "high",
        cases: nonTraitesAnciens.sort((a, b) => {
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        }),
        description: "Demandes en attente de traitement depuis plus de 24 heures",
      });
    }

    // 💰 SECTION 3: Acompte en attente (événement proche)
    const acompteEnAttente = filteredCases.filter((c) => {
      const daysUntilEvent = getDaysUntilEvent(c.event_date);
      // On vérifie si le dossier est signé mais pas encore "Acompte reçu"
      return (
        c.status === "Signé" &&
        daysUntilEvent !== null &&
        daysUntilEvent >= 0 &&
        daysUntilEvent <= 1 // Événement demain ou aujourd'hui
      );
    });

    if (acompteEnAttente.length > 0) {
      sections.push({
        title: "Acompte en attente (événement proche)",
        icon: "💰",
        priority: "critical",
        cases: acompteEnAttente.sort((a, b) => {
          const daysA = getDaysUntilEvent(a.event_date) || 999;
          const daysB = getDaysUntilEvent(b.event_date) || 999;
          return daysA - daysB;
        }),
        description: "Devis signé mais acompte non reçu - Événement dans moins de 24h",
      });
    }

    // 📞 SECTION 4: Relance téléphonique prévue aujourd'hui
    const todayParis = getParisDateKey(new Date());
    const aRelancer = filteredCases.filter(
      (c) => c.phone_reminder_date === todayParis
    );

    if (aRelancer.length > 0) {
      sections.push({
        title: "À relancer aujourd'hui",
        icon: "📞",
        priority: "medium",
        cases: aRelancer.sort((a, b) => {
          const daysA = getDaysUntilEvent(a.event_date) || 999;
          const daysB = getDaysUntilEvent(b.event_date) || 999;
          return daysA - daysB;
        }),
        description: "Dossiers avec relance téléphonique prévue aujourd'hui",
      });
    }

    // ✅ SECTION 5: Événements de la semaine confirmés
    const evenementsSemaineConfirmes = filteredCases.filter((c) => {
      const daysUntilEvent = getDaysUntilEvent(c.event_date);
      return (
        c.status === "Acompte reçu" &&
        daysUntilEvent !== null &&
        daysUntilEvent >= 0 &&
        daysUntilEvent <= 7
      );
    });

    if (evenementsSemaineConfirmes.length > 0) {
      sections.push({
        title: "Événements de la semaine confirmés",
        icon: "✅",
        priority: "info",
        cases: evenementsSemaineConfirmes.sort((a, b) => {
          const daysA = getDaysUntilEvent(a.event_date) || 999;
          const daysB = getDaysUntilEvent(b.event_date) || 999;
          return daysA - daysB;
        }),
        description: "Événements confirmés dans les 7 prochains jours",
      });
    }

    return sections;
  }, [filteredCases]);

  const totalTodos = useMemo(() => {
    return todoSections.reduce((sum, section) => sum + section.cases.length, 0);
  }, [todoSections]);

  const handleMarkAsTreated = async (caseId: string) => {
    // Marquer comme lu pour retirer de la liste des non lus
    await supabase.rpc("mark_case_as_read", { p_case_id: caseId });
    
    // Optionnel: Vous pouvez aussi changer le statut si nécessaire
    // await supabase.from("cases").update({ status: "Déjà relancé" }).eq("id", caseId);
  };

  const formatEventDate = (dateStr: string | null): string => {
    if (!dateStr) return "Date non définie";
    const diffDays = diffParisCalendarDays(new Date(), dateStr);

    if (diffDays === 0) return "Aujourd'hui";
    if (diffDays === 1) return "Demain";
    if (diffDays === -1) return "Hier";
    if (diffDays < 0) return `Il y a ${Math.abs(diffDays)} jour${Math.abs(diffDays) > 1 ? "s" : ""}`;
    return `Dans ${diffDays} jour${diffDays > 1 ? "s" : ""}`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical": return "bg-red-50 border-red-200";
      case "high": return "bg-orange-50 border-orange-200";
      case "medium": return "bg-yellow-50 border-yellow-200";
      case "info": return "bg-green-50 border-green-200";
      default: return "bg-gray-50 border-gray-200";
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-500">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-3xl">🔥</span>
                <div>
                  <h2 className="text-xl font-bold text-white">
                    À faire aujourd'hui
                  </h2>
                  <p className="text-sm text-white/90 mt-0.5">
                    {totalTodos} action{totalTodos > 1 ? "s" : ""} en attente
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {todoSections.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Tout est sous contrôle !
                </h3>
                <p className="text-gray-600">
                  Aucune action urgente en attente pour aujourd'hui.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {todoSections.map((section, sectionIdx) => (
                  <div
                    key={sectionIdx}
                    className={`border-2 rounded-lg p-4 ${getPriorityColor(section.priority)}`}
                  >
                    {/* Section header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{section.icon}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">
                            {section.title}
                          </h3>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {section.description}
                          </p>
                        </div>
                      </div>
                      <span className="bg-white px-2 py-1 rounded-full text-xs font-bold text-gray-700">
                        {section.cases.length}
                      </span>
                    </div>

                    {/* Cases list */}
                    <div className="space-y-2">
                      {section.cases.map((caseData) => (
                        <div
                          key={caseData.id}
                          className="bg-white rounded-md p-3 border border-gray-200 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-sm text-gray-900 truncate">
                                {caseData.company_name || caseData.title}
                              </div>
                              <div className="text-xs text-gray-600 mt-0.5">
                                {caseData.client_first_name} {caseData.client_last_name}
                              </div>
                              {caseData.event_date && (
                                <div className="text-xs text-gray-500 mt-1">
                                  📅 {formatEventDate(caseData.event_date)}
                                </div>
                              )}
                              {caseData.center && (
                                <span className="inline-block mt-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                                  {caseData.center}
                                </span>
                              )}
                            </div>

                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={() => onCaseClick(caseData)}
                                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
                                title="Ouvrir le dossier"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              <button
                                onClick={() => handleMarkAsTreated(caseData.id)}
                                className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                                title="Marquer comme traité"
                              >
                                ✓ Traité
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
